"use client";
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import "./QuestionCard.css";
import { useTranslation } from "react-i18next";
import ConfirmDeleteModal from "../ConfirmDeleteModal/ConfirmDeleteModal";
import { useSelector, useDispatch } from "react-redux";
import useComments from "../../../hooks/useComments";
import { useUserInformation } from "../../../../Profile/hooks/useUserInformation";
import { resolveDoctorAvatar } from "../../../../ProfileDoctor/hooks/useDoctorInformation";
import { fetchUserById } from "../../../../Profile/redux/thunks/userThunks";
import { fetchDoctors } from "../../../../authDoctor/redux/thunks/doctorsThunks";
import AIResponseBanner from "../../shared/AIResponseBanner/AIResponseBanner";

/* ─────────────── Constants ─────────────── */
const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
const DOCTOR_DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/3774/3774299.png";
const AI_BOT_AVATAR = "https://cdn-icons-png.flaticon.com/512/4712/4712035.png";

/* ─────────────── Helpers ─────────────── */
const pick = (...vals) =>
  vals.find((v) => {
    if (v == null) return false;
    const s = String(v).trim();
    return s.length > 0 && s.toLowerCase() !== "undefined" && s.toLowerCase() !== "null";
  });

const safeStr = (v) => (v == null ? "" : String(v)).trim();

const isDoctorLike = (role) => {
  const r = String(role || "").toLowerCase();
  return r.includes("doctor") || r.includes("médecin") || r.includes("medecin") || r.includes("dr");
};

const isNumericId = (v) => /^\d+$/.test(String(v ?? "").trim());

const MAX_DATA_URL_LEN = 2_000_000;
const getMediaUrl = (photo) => {
  if (!photo) return null;
  try {
    const s = String(photo).trim();
    if (s.startsWith("data:image/")) {
      if (s.length < 50 || s.length > MAX_DATA_URL_LEN) return null;
      return s;
    }
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    if (/^[A-Za-z0-9+/]+=*$/.test(s) && s.length > 100 && s.length < MAX_DATA_URL_LEN) {
      const clean = s.replace(/[^A-Za-z0-9+/=]/g, "");
      atob(clean.slice(0, 64));
      return `data:image/jpeg;base64,${clean}`;
    }
    return null;
  } catch {
    return null;
  }
};

const timeAgo = (d, t) => {
  if (!d) return t?.("postCard.now") || "now";
  const secs = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  const map = [
    ["y", 31536000], ["mo", 2592000], ["w", 604800],
    ["d", 86400], ["h", 3600], ["m", 60],
  ];
  for (const [k, v] of map) {
    const n = Math.floor(secs / v);
    if (n >= 1) return `${n}${k}`;
  }
  return t?.("postCard.now") || "now";
};

/* ─────────────── SafeImage ─────────────── */
const SafeImage = ({ src, fallback, alt, className }) => {
  const [err, setErr] = useState(false);
  const finalSrc = useMemo(() => {
    const media = getMediaUrl(src);
    if (media) return media;
    return fallback || DEFAULT_AVATAR;
  }, [src, fallback]);
  useEffect(() => setErr(false), [finalSrc]);
  return (
    <img
      className={className}
      src={err ? fallback || DEFAULT_AVATAR : finalSrc}
      alt={alt || "image"}
      loading="lazy"
      onError={() => setErr(true)}
    />
  );
};

/* ─────────────── extractCommentActor ─────────────── */
const extractCommentActor = (c) => {
  const doctorId =
    c?.doctorId ?? c?.doctor_id ?? c?.doctor?.id ?? c?.doctor?._id ??
    c?.doctor?.doctorId ?? c?.doctor?.pgId ?? c?.author?.doctorId ?? null;

  const embeddedDoctor = c?.doctor ?? c?.Doctor ?? c?.author?.doctor ?? null;
  const authorRole = c?.author?.role ?? c?.doctor?.role ?? c?.user?.role ?? c?.role ?? null;
  const isDoctorByRole = authorRole && isDoctorLike(authorRole);

  if (doctorId != null || embeddedDoctor != null || isDoctorByRole) {
    return { type: "doctor", id: doctorId != null ? String(doctorId) : null, embedded: embeddedDoctor };
  }

  const uid =
    c?.userId ?? c?.user_id ?? c?.authorId ?? c?.createdById ??
    c?.user?.id ?? c?.user?._id ?? c?.user?.userId ?? c?.author?.id ?? null;

  return {
    type: "user",
    id: uid != null ? String(uid) : null,
    embedded: c?.user ?? c?.User ?? c?.author ?? null,
  };
};

const isDoctorComment = (comment) => extractCommentActor(comment).type === "doctor";

/* ─────────────── Name builders ─────────────── */
const buildDoctorName = (obj, fallback = "Doctor") => {
  if (!obj) return fallback;
  const name = pick(
    obj.fullName, obj.name, obj.doctorName,
    obj.firstName && obj.lastName ? `${obj.firstName} ${obj.lastName}`.trim() : null,
    obj.user?.fullName,
    obj.user?.firstName && obj.user?.lastName ? `${obj.user.firstName} ${obj.user.lastName}`.trim() : null,
    obj.firstName, obj.lastName, obj.user?.firstName, obj.user?.lastName
  );
  if (!name) return fallback;
  const n = String(name).trim();
  return n.toLowerCase().startsWith("dr") ? n : `Dr. ${n}`;
};

const buildUserName = (obj, fallback = "Member") => {
  if (!obj) return fallback;
  const name = pick(
    obj.fullName, obj.name,
    obj.firstName && obj.lastName ? `${obj.firstName} ${obj.lastName}`.trim() : null,
    obj.firstName, obj.lastName,
    obj.user?.fullName,
    obj.user?.firstName && obj.user?.lastName ? `${obj.user.firstName} ${obj.user.lastName}`.trim() : null
  );
  return name || fallback;
};

/* ─────────────── Avatar resolvers ─────────────── */
const resolveDoctorAvatarGuarded = (doc) => {
  if (!doc || typeof doc !== "object") return DOCTOR_DEFAULT_AVATAR;
  const raw = doc?.avatarUrl ?? doc?.avatar ?? doc?.photo ?? doc?.image ?? null;
  if (!raw || isNumericId(raw)) return DOCTOR_DEFAULT_AVATAR;
  const resolved = resolveDoctorAvatar(raw);
  return resolved || DOCTOR_DEFAULT_AVATAR;
};

const resolvePatientAvatar = (hookAvatarSrc, obj) => {
  const fromHook = getMediaUrl(hookAvatarSrc);
  if (fromHook) return fromHook;
  const raw = pick(obj?.avatar, obj?.avatarUrl, obj?.photo, obj?.profileImage, obj?.profile_image, obj?.picture);
  return getMediaUrl(raw) || DEFAULT_AVATAR;
};

/* ─────────────── CommentRow ─────────────── */
function CommentRow({
  c, actor, authorObj, embeddedData, isDoctorAuthor, t,
  viewerId, openMenuId, setOpenMenuId,
  editingId, editingValue, setEditingValue,
  onStartEdit, onCancelEdit, onSaveEdit, onDelete,
}) {
  const commentId = String(c?._id || c?.id || "");
  const isOwner = viewerId && actor?.id && String(actor.id) === String(viewerId);
  const isEditing = editingId && String(editingId) === commentId;

  const effectiveAuthor = useMemo(() => {
    const a = authorObj && Object.keys(authorObj).length ? authorObj : null;
    const e = embeddedData && typeof embeddedData === "object" ? embeddedData : null;
    const direct = isDoctorAuthor
      ? (c?.doctor ?? c?.Doctor ?? c?.author)
      : (c?.user ?? c?.User ?? c?.author);
    return a || e || (typeof direct === "object" ? direct : null) || null;
  }, [authorObj, embeddedData, c, isDoctorAuthor]);

  const { avatarSrc: hookAvatarSrc } = useUserInformation(isDoctorAuthor ? null : effectiveAuthor);

  const authorName = useMemo(() =>
    isDoctorAuthor
      ? buildDoctorName(effectiveAuthor, t?.("postCard.doctor") || "Doctor")
      : buildUserName(effectiveAuthor, t?.("postCard.user") || "User"),
    [effectiveAuthor, isDoctorAuthor, t]
  );

  const roleLabel = useMemo(
    () => isDoctorAuthor ? "Doctor" : (effectiveAuthor?.role || effectiveAuthor?.user?.role || "Patient"),
    [isDoctorAuthor, effectiveAuthor]
  );

  const avatarFinal = useMemo(() => {
    if (isDoctorAuthor) return resolveDoctorAvatarGuarded(effectiveAuthor || actor?.embedded || null);
    const patient = effectiveAuthor || c?.user || c?.User || c?.author || actor?.embedded;
    return resolvePatientAvatar(hookAvatarSrc, patient);
  }, [c, actor, isDoctorAuthor, effectiveAuthor, hookAvatarSrc]);

  return (
    <div className="pcc-item" id={`comment-${commentId}`}>
      <div className="pcc-left">
        <SafeImage
          src={avatarFinal}
          fallback={isDoctorAuthor ? DOCTOR_DEFAULT_AVATAR : DEFAULT_AVATAR}
          alt={authorName}
          className="pcc-av"
        />
      </div>

      <div className="pcc-body">
        <div className="pcc-header">
          <div className="pcc-info">
            <span className={`pcc-name ${isDoctorAuthor ? "pcc-name-doctor" : ""}`}>{authorName}</span>
            <span className={`pcc-role ${isDoctorAuthor ? "pcc-role-doctor" : ""}`}>{roleLabel}</span>
            <span className="pcc-time">{timeAgo(c?.createdAt, t)}</span>
          </div>

          {isOwner && !!commentId && (
            <div className="pcc-menu-wrapper" data-comment-menu={commentId}>
              <button
                type="button"
                className="pcc-menu-btn"
                aria-haspopup="menu"
                aria-expanded={openMenuId === commentId}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenuId((prev) => (prev === commentId ? null : commentId));
                }}
                title={t?.("postCard.options") || "Options"}
              >
                •••
              </button>
              <div className={`pcc-menu ${openMenuId === commentId ? "open" : ""}`} role="menu">
                <button className="pcc-menu-item" role="menuitem"
                  onClick={() => onStartEdit(commentId, c?.content || c?.text || "")}>
                  {t?.("postCard.edit") || "Edit"}
                </button>
                <button className="pcc-menu-item danger" role="menuitem" onClick={() => onDelete(commentId)}>
                  {t?.("postCard.delete") || "Delete"}
                </button>
              </div>
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="pcc-edit">
            <textarea
              className="pcc-edit-input"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              rows={2}
              autoFocus
            />
            <div className="pcc-edit-actions">
              <button type="button" className="pcc-edit-btn"
                onClick={() => onSaveEdit(commentId)} disabled={!safeStr(editingValue)}>
                {t?.("common.save") || "Save"}
              </button>
              <button type="button" className="pcc-edit-btn ghost" onClick={onCancelEdit}>
                {t?.("common.cancel") || "Cancel"}
              </button>
            </div>
          </div>
        ) : (
          <div className="pcc-text">{c?.content || c?.text || ""}</div>
        )}
      </div>
    </div>
  );
}

/* ─────────────── Main Component ─────────────── */
export default function QuestionCard({
  aiResponseText,
  id,
  ownerId,
  author,
  status = "Answered",
  postedAt = "Date inconnue",
  text,
  answersCount = 0,
  attachments = [],
  answers = [],
  onAnswers,
  onDelete,
  onEdit,
  onTranslate,
  canDelete = false,
  currentUserId,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const viewerId = currentUserId != null ? String(currentUserId) : "";

  const [openConfirm, setOpenConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(false);
  const [editingQuestionValue, setEditingQuestionValue] = useState(text || "");
  const editInputRef = useRef(null);
  const menuRef = useRef(null);
  const [showAnswers, setShowAnswers] = useState(false);

  const [openCommentMenuId, setOpenCommentMenuId] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const isAnswered = String(status || "").toLowerCase() === "answered";

  const statusLabel = useMemo(() => {
    const s = String(status || "").toLowerCase();
    if (s === "answered") return t("questions.card.status.answered");
    if (s === "pending") return t("questions.card.status.pending");
    return status;
  }, [status, t]);

  const {
    comments,
    isLoading: commentsLoading,
    commentsCount,
    fetchComments,
    editComment,
    removeComment,
  } = useComments(id);

  useEffect(() => {
    if (!id) return;
    fetchComments();
  }, [id, fetchComments]);

  useEffect(() => {
    if (showAnswers && id) fetchComments();
  }, [showAnswers, id, fetchComments]);

  const realAnswersCount = useMemo(() => {
    if (typeof commentsCount === "number") return commentsCount;
    if (Array.isArray(comments) && comments.length > 0) return comments.length;
    return answersCount ?? 0;
  }, [commentsCount, comments, answersCount]);

  const orderedComments = useMemo(() => {
    if (!Array.isArray(comments)) return [];
    return comments
      .map((comment, index) => ({ comment, index }))
      .sort((left, right) => {
        const doctorOrder =
          Number(isDoctorComment(right.comment)) -
          Number(isDoctorComment(left.comment));
        return doctorOrder || left.index - right.index;
      })
      .map(({ comment }) => comment);
  }, [comments]);

  const toggleAnswers = () => {
    setShowAnswers((p) => !p);
    onAnswers?.(id);
  };

  useEffect(() => {
    if (!openCommentMenuId) return;
    const onDown = (e) => {
      if (!e.target.closest?.(`[data-comment-menu="${openCommentMenuId}"]`)) setOpenCommentMenuId(null);
    };
    const onEsc = (e) => e.key === "Escape" && setOpenCommentMenuId(null);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [openCommentMenuId]);

  const allUsers = useSelector((state) => state.user?.users || {});
  const doctorsFromRedux = useSelector(
    (state) => state.doctors?.doctors || state.doctors?.items || state.doctor?.doctors || []
  );

  const doctorsFetchedRef = useRef(false);
  const fetchInitiatedRef = useRef(new Set());
  const commentsIdsRef = useRef("");

  const doctorsMap = useMemo(() => {
    if (doctorsFromRedux && !Array.isArray(doctorsFromRedux) && typeof doctorsFromRedux === "object") {
      return doctorsFromRedux;
    }
    if (Array.isArray(doctorsFromRedux)) {
      const map = {};
      doctorsFromRedux.forEach((d) => {
        const did = String(d?.id || d?._id || d?.doctorId || d?.userId || "");
        if (did) map[did] = d;
      });
      return map;
    }
    return {};
  }, [doctorsFromRedux]);

  useEffect(() => {
    if (doctorsFetchedRef.current) return;
    doctorsFetchedRef.current = true;
    dispatch(fetchDoctors()).catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    if (!Array.isArray(comments) || comments.length === 0) return;
    const currentIds = comments.map((c) => c?._id || c?.id).filter(Boolean).sort().join(",");
    if (commentsIdsRef.current === currentIds) return;
    commentsIdsRef.current = currentIds;

    const usersToFetch = new Set();
    for (const c of comments) {
      const actor = extractCommentActor(c);
      if (actor.type === "user" && actor.id) {
        const uid = String(actor.id);
        const key = `user:${uid}`;
        if (allUsers?.[uid] || fetchInitiatedRef.current.has(key)) continue;
        usersToFetch.add(uid);
      }
    }
    usersToFetch.forEach((uid) => {
      fetchInitiatedRef.current.add(`user:${uid}`);
      dispatch(fetchUserById(uid));
    });
  }, [comments, dispatch, allUsers]);

  const startEditComment = useCallback((cid, txt) => {
    setEditingCommentId(String(cid));
    setEditingValue(String(txt || ""));
    setOpenCommentMenuId(null);
  }, []);

  const cancelEditComment = useCallback(() => {
    setEditingCommentId(null);
    setEditingValue("");
  }, []);

  const saveEditComment = useCallback(async (cid) => {
    const txt = safeStr(editingValue);
    if (!cid || !txt) return;
    await editComment(String(cid), txt);
    cancelEditComment();
    fetchComments().catch(() => {});
  }, [editingValue, editComment, cancelEditComment, fetchComments]);

  const deleteOneComment = useCallback(async (cid) => {
    if (!cid) return;
    const ok = window.confirm(t?.("posts.confirmDeleteComment") || "Delete this comment?");
    if (!ok) return;
    await removeComment(String(cid));
    setOpenCommentMenuId(null);
    fetchComments().catch(() => {});
  }, [removeComment, fetchComments, t]);

  const askDelete = () => {
    setMenuOpen(false);
    setOpenConfirm(true);
  };

  const askEdit = () => {
    setMenuOpen(false);
    setEditingQuestionValue(text || "");
    setEditingQuestion(true);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (event) => {
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    const onEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEscape);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (editingQuestion) editInputRef.current?.focus();
  }, [editingQuestion]);

  const confirmDelete = async () => {
    try {
      setLoading(true);
      if (!id) return;
      await onDelete?.(id, ownerId);
    } finally {
      setLoading(false);
      setOpenConfirm(false);
    }
  };

  const saveEdit = async (newText) => {
    try {
      setLoading(true);
      await onEdit?.(id, newText);
    } finally {
      setLoading(false);
      setEditingQuestion(false);
    }
  };

  const saveQuestionEdit = () => {
    const value = editingQuestionValue.trim();
    if (!value || loading) return;
    saveEdit(value);
  };

  return (
    <article className="qc-card" id={`post-${id}`}>
      <div className="qc-surface">
        <header className="qc-head">
          {author && <span className="qc-author">{author}</span>}
          <span className="qc-posted">{postedAt}</span>
          <div className="qc-menu-wrapper" ref={menuRef}>
            <button
              type="button"
              className="qc-menu-button"
              aria-label="Options"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              •••
            </button>
            {menuOpen && (
              <div className="qc-menu" role="menu">
                <button type="button" className="qc-menu-item" onClick={askEdit}>
                  Modifier
                </button>
                {canDelete && (
                  <button type="button" className="qc-menu-item danger" onClick={askDelete}>
                    Supprimer
                  </button>
                )}
              </div>
            )}
          </div>
        </header>

        <span className={`qc-status ${isAnswered ? "is-answered" : "is-pending"}`}>
          <span className="qc-dot" />
          {statusLabel}
        </span>

        <div className="qc-body">
          {editingQuestion ? (
            <div className="qc-edit-area">
              <textarea
                ref={editInputRef}
                className="qc-edit-input"
                value={editingQuestionValue}
                onChange={(event) => setEditingQuestionValue(event.target.value)}
                disabled={loading}
              />
              <div className="qc-edit-actions">
                <button
                  type="button"
                  className="qc-edit-cancel"
                  onClick={() => {
                    setEditingQuestion(false);
                    setEditingQuestionValue(text || "");
                  }}
                  disabled={loading}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="qc-edit-save"
                  onClick={saveQuestionEdit}
                  disabled={!editingQuestionValue.trim() || loading}
                >
                  {loading ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </div>
          ) : (
            <p className="qc-text">{text}</p>
          )}

          {!!attachments?.length && (
            <div className={`qc-media ${attachments.length > 1 ? "is-grid" : "is-single"}`}>
              {attachments.map((att, i) => (
                <img
                  key={i}
                  className="qc-img"
                  src={att?.url || att}
                  alt={att?.name || t("questions.card.attachmentAlt", { index: i + 1 })}
                  loading="lazy"
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <footer className="qc-foot">
        <div className="qc-actions">
          <button className="qc-action" onClick={toggleAnswers}>
            {t("questions.card.actions.answers")}
          </button>
        </div>
        <div className="qc-right">
          {/* ✅ FIX: use realAnswersCount instead of answersCount prop */}
          <span className="qc-count">
            {t("questions.card.answersCount", { count: realAnswersCount })}
          </span>
        </div>
      </footer>

      {showAnswers && (
        <div className="qc-answers">
          {commentsLoading ? (
            <div className="qc-answers-loading">{t("questions.card.loadingAnswers")}</div>
          ) : (
            <>
              {/* ✅ Réponse IA en premier */}
              {aiResponseText && (
                <AIResponseBanner text={aiResponseText} t={t} />
              )}

              {/* Séparateur si IA + commentaires */}
              {aiResponseText && Array.isArray(comments) && comments.length > 0 && (
                <div className="pcc-ai-divider">
                  <span>{t?.("postCard.memberComments") || "Commentaires des membres"}</span>
                </div>
              )}

              {/* Commentaires membres */}
              {orderedComments.length > 0 ? (
                orderedComments.map((c) => {
                  const actor = extractCommentActor(c);
                  const isDoctorAuthor = actor?.type === "doctor";
                  const doctorId = isDoctorAuthor && actor?.id ? String(actor.id) : null;
                  const userId2 = !isDoctorAuthor && actor?.id ? String(actor.id) : null;
                  const authorObj = isDoctorAuthor
                    ? (doctorId ? doctorsMap[doctorId] : null)
                    : (userId2 ? allUsers[userId2] : null);

                  return (
                    <CommentRow
                      key={c?._id || c?.id}
                      c={c}
                      actor={actor}
                      authorObj={authorObj}
                      embeddedData={actor?.embedded}
                      isDoctorAuthor={isDoctorAuthor}
                      t={t}
                      viewerId={viewerId}
                      openMenuId={openCommentMenuId}
                      setOpenMenuId={setOpenCommentMenuId}
                      editingId={editingCommentId}
                      editingValue={editingValue}
                      setEditingValue={setEditingValue}
                      onStartEdit={startEditComment}
                      onCancelEdit={cancelEditComment}
                      onSaveEdit={saveEditComment}
                      onDelete={deleteOneComment}
                    />
                  );
                })
              ) : (
                !commentsLoading && !aiResponseText && (
                  <div className="qc-answers-empty">{t("questions.card.noAnswersYet")}</div>
                )
              )}

              {/* Vide mais IA présente : pas de "no answers" */}
              {!commentsLoading && aiResponseText && Array.isArray(comments) && comments.length === 0 && null}
            </>
          )}
        </div>
      )}

      <ConfirmDeleteModal
        open={openConfirm}
        loading={loading}
        onConfirm={confirmDelete}
        onClose={() => setOpenConfirm(false)}
      />
    </article>
  );
}